'use client';
import React, { useState, useEffect } from 'react';
import { courseListsApi } from '@/app/services/api';
import { CourseList } from '@/app/types/course';
import { useAuth } from '@/app/Components/AuthProvider';
import { MagnifyingGlassIcon, DocumentDuplicateIcon, StarIcon, BookOpenIcon, FunnelIcon } from '@heroicons/react/24/outline';
import CourseName from '@/app/Components/CourseName';
import { CourseLink } from '@/app/Components/CoursePeek/CourseLink';
import { useRouter, useSearchParams } from 'next/navigation';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { SEMESTER_OPTIONS } from '@/app/lib/semesters';
import { LoadingBubbles } from '@/app/Components/LoadingBubbles';

type FilterType = 'all' | 'official' | 'unofficial';

export default function PublicCourseLists() {
    const [publicLists, setPublicLists] = useState<CourseList[]>([]);
    const [allLists, setAllLists] = useState<CourseList[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<FilterType>('all');
    const [displayCount, setDisplayCount] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [duplicatingListId, setDuplicatingListId] = useState<string | null>(null);
    const { user } = useAuth();
    const router = useRouter();
    const requestedSemester = useSearchParams().get('semester');
    const { activeSemester } = useUiPreferences();
    const semester = SEMESTER_OPTIONS.find(({ id }) => id === requestedSemester)?.id ?? activeSemester;

    useEffect(() => {
        loadPublicLists();
    }, []);

    useEffect(() => {
        filterAndSearchLists();
    }, [allLists, searchTerm, filterType, displayCount]);

    const loadPublicLists = async () => {
        try {
            setIsLoading(true);
            setError('');
            const lists = await courseListsApi.get_public_course_lists() as CourseList[];
            setAllLists(lists || []);
        } catch (err) {
            console.error('Error loading public course lists:', err);
            setError('Failed to load public course lists');
        } finally {
            setIsLoading(false);
        }
    };

    const filterAndSearchLists = () => {
        let filtered = [...allLists];

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(list =>
                list.name.toLowerCase().includes(searchLower) ||
                (list.description && list.description.toLowerCase().includes(searchLower))
            );
        }

        // Apply type filter
        switch (filterType) {
            case 'official':
                filtered = filtered.filter(list => isOfficialList(list));
                break;
            case 'unofficial':
                filtered = filtered.filter(list => !isOfficialList(list));
                break;
            case 'all':
            default:
                // No additional filtering needed
                break;
        }

        // Limit to display count
        setPublicLists(filtered.slice(0, displayCount));
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        // Search is now handled by useEffect
    };

    const handleFilterChange = (newFilter: FilterType) => {
        setFilterType(newFilter);
        setDisplayCount(20); // Reset display count when filter changes
    };

    const handleShowMore = () => {
        setDisplayCount(prev => prev + 20);
    };

    const handleDuplicate = async (listId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent card click when clicking duplicate button
        if (!user?.id) {
            setError('Please log in to duplicate course lists');
            return;
        }

        try {
            setDuplicatingListId(listId);
            await courseListsApi.duplicate(parseInt(listId), user.id);
            // Refresh the page or show success message
            window.location.reload();
        } catch (err) {
            console.error('Error duplicating course list:', err);
            setError('Failed to duplicate course list');
        } finally {
            setDuplicatingListId(null);
        }
    };

    const handleViewList = (listId: string) => {
        const query = new URLSearchParams({ id: listId });
        if (SEMESTER_OPTIONS.some(({ id }) => id === semester)) query.set('semester', semester);
        router.push(`/course-list?${query}`);
    };

    const isOfficialList = (list: CourseList) => {
        return list.user_generated === null;
    };

    const getFilteredCount = () => {
        let filtered = [...allLists];
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(list =>
                list.name.toLowerCase().includes(searchLower) ||
                (list.description && list.description.toLowerCase().includes(searchLower))
            );
        }
        switch (filterType) {
            case 'official':
                return filtered.filter(list => isOfficialList(list)).length;
            case 'unofficial':
                return filtered.filter(list => !isOfficialList(list)).length;
            default:
                return filtered.length;
        }
    };

    const totalFilteredCount = getFilteredCount();
    const hasMoreResults = totalFilteredCount > displayCount;

    return (
        <div className="card">
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h2 className="flex items-center text-xl font-bold text-slate-900 dark:text-white">
                        <BookOpenIcon className="mr-2 h-6 w-6 text-red-600" />
                        Discover Public Course Lists
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        Duplicate official and community-curated collections into your own planning workspace.
                    </p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {totalFilteredCount} visible lists
                </div>
            </div>

            {/* Search and Filter Form */}
            <form onSubmit={handleSearch} className="mb-8 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-card border border-slate-100 dark:border-slate-800">
                <div className="flex flex-col gap-4">
                    {/* Search Bar */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1 relative group">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search for course lists by name or description..."
                                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-control border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all shadow-e1"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary"
                        >
                            {isLoading ? 'Searching...' : 'Search'}
                        </button>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 hide-scrollbar">
                            <FunnelIcon className="h-5 w-5 text-slate-500 mr-1" />
                            <div className="flex bg-slate-200 dark:bg-slate-800 p-1 rounded-control">
                                <button
                                    type="button"
                                    onClick={() => handleFilterChange('all')}
                                    className={`px-3 py-1.5 rounded-control text-sm font-medium transition-all ${filterType === 'all'
                                        ? 'bg-white dark:bg-slate-700 text-red-600 dark:text-red-400 shadow-e1'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleFilterChange('official')}
                                    className={`px-3 py-1.5 rounded-control text-sm font-medium transition-all flex items-center gap-1.5 ${filterType === 'official'
                                        ? 'bg-white dark:bg-slate-700 text-red-600 dark:text-red-400 shadow-e1'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                >
                                    <StarIcon className="h-3.5 w-3.5" />
                                    Official
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleFilterChange('unofficial')}
                                    className={`px-3 py-1.5 rounded-control text-sm font-medium transition-all ${filterType === 'unofficial'
                                        ? 'bg-white dark:bg-slate-700 text-red-600 dark:text-red-400 shadow-e1'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                >
                                    Community
                                </button>
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="text-sm text-slate-500 font-medium">
                            {totalFilteredCount} results found
                        </div>
                    </div>
                </div>
            </form>

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300 rounded-control p-4 text-sm font-medium border border-red-100 dark:border-red-800">
                    {error}
                </div>
            )}

            {/* Public Lists */}
            <div className="space-y-4">
                {publicLists.length === 0 && !isLoading ? (
                    <div className="text-center py-16 bg-slate-50 dark:bg-slate-900/30 rounded-card border border-dashed border-slate-200 dark:border-slate-700">
                        <BookOpenIcon className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                        <p className="text-slate-900 dark:text-white font-medium mb-1">No lists found</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                            {searchTerm
                                ? `No results for "${searchTerm}". Try broadening your search.`
                                : 'Try adjusting filters to browse more lists.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {publicLists.map((list) => (
                            <div
                                key={list.id}
                                onClick={() => handleViewList(list.id)}
                                className={`group relative rounded-card border p-5 transition-all duration-300 hover:shadow-e2 cursor-pointer ${isOfficialList(list)
                                    ? 'bg-gradient-to-r from-red-50 to-white dark:from-red-900/10 dark:to-slate-800 border-red-100 dark:border-red-900/30 hover:border-red-200 dark:hover:border-red-800'
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-red-300 dark:hover:border-red-700'
                                    }`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <h3 className="font-bold text-lg text-slate-900 dark:text-white truncate pr-4 group-hover:text-red-600 transition-colors">
                                                {list.name}
                                            </h3>
                                            {isOfficialList(list) && (
                                                <span className="shrink-0 inline-flex items-center gap-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 rounded-full text-xs font-semibold border border-red-200 dark:border-red-800">
                                                    <StarIcon className="h-3 w-3" />
                                                    Official
                                                </span>
                                            )}
                                        </div>

                                        {list.description && (
                                            <p className="text-slate-600 dark:text-slate-400 text-sm mb-3 line-clamp-2">
                                                {list.description}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-3">
                                            <span className="flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300">
                                                <BookOpenIcon className="h-3.5 w-3.5" />
                                                {list.classes?.length ?? 0} courses
                                            </span>
                                            <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                                            {list.created_at && (
                                                <span>
                                                    Updated {new Date(list.created_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>

                                        {/* Course Preview */}
                                        {list.classes && list.classes.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {list.classes.slice(0, 4).map((courseId) => (
                                                    <span
                                                        key={courseId}
                                                        className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600"
                                                    >
                                                        <CourseLink courseId={courseId} context={{ label: 'In a public course list', facts: [{ label: 'List', value: list.name }] }}><CourseName code={courseId} /></CourseLink>
                                                    </span>
                                                ))}
                                                {list.classes.length > 4 && (
                                                    <span className="text-xs text-slate-400 px-1 pt-1">
                                                        +{list.classes.length - 4}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="shrink-0">
                                        <button
                                            type="button"
                                            onClick={(e) => handleDuplicate(list.id, e)}
                                            disabled={duplicatingListId === list.id || !user?.id}
                                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-control text-sm font-medium transition-all bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 hover:text-red-600 hover:border-red-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-e1"
                                        >
                                            {duplicatingListId === list.id ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current"></div>
                                                    <span>Copying...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                                    <span>Duplicate</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Show More Button */}
            {hasMoreResults && (
                <div className="mt-8 text-center">
                    <button
                        type="button"
                        onClick={handleShowMore}
                        className="btn-secondary"
                    >
                        Load More Results
                    </button>
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center py-12">
                    <LoadingBubbles size="compact" className="mb-3" />
                    <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">Loading public lists...</span>
                </div>
            )}
        </div>
    );
}
